import { DEFAULT_CURRENCY_CODE } from '../../constants/currency.const';
import { cn } from '../../lib/utils';
import { formatCurrency } from '../../utils/fiat-currency.util';
import CoinIcon from '../coinIcon';

interface Props {
  className?: string;
  brandName?: string;
  isBrand?: boolean;
  rewardName?: string;
  rewardPoints?: number;
  rewardTotalPoints?: number | string;
  rewardAvailablePoints?: number | null;
  description?: string;
  tier?: string;
  themeColor?: string;
  backgroundImage?: string;
  valueApplied?: string;
  steppedCreditPerRm?: number;
  subDescription?: string | null;
  currencyCode?: string;
  isHighestTier?: boolean;
  isCurrentTier?: boolean;
  isAboveCurrentTier?: boolean;
  hideStatusPill?: boolean;
}

const StatusPill = ({
  isLocked,
  isCurrent,
  themeColor
}: {
  isLocked: boolean;
  isCurrent: boolean;
  themeColor: string;
}) => {
  if (isLocked) {
    return (
      <span className="flex items-center gap-1 rounded-md bg-gray-400 px-2 py-1 text-sm font-medium text-white">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="size-4"
        >
          <path
            fillRule="evenodd"
            d="M10 1a4.5 4.5 0 0 0-4.5 4.5V9H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-.5V5.5A4.5 4.5 0 0 0 10 1Zm3 8V5.5a3 3 0 1 0-6 0V9h6Z"
            clipRule="evenodd"
          />
        </svg>
        Locked
      </span>
    );
  }
  if (isCurrent) {
    return (
      <span
        className="flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-white"
        style={{ backgroundColor: themeColor }}
      >
        Current
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 rounded-md bg-green-500 px-2 py-1 text-sm font-medium text-white">
      Mastered
    </span>
  );
};

function MembershipCard({
  className,
  brandName = 'AyeFace',
  isBrand = false,
  rewardName = 'Level 1',
  rewardPoints = 330,
  rewardTotalPoints = 1000,
  rewardAvailablePoints = null,
  description = 'Earn points when you shop at AyeFace',
  subDescription = null,
  themeColor = '#FE724C',
  valueApplied = '1',
  steppedCreditPerRm = 1,
  currencyCode = DEFAULT_CURRENCY_CODE,
  isCurrentTier = false,
  isAboveCurrentTier = false,
  hideStatusPill = false
}: Readonly<Props>) {
  const activeColor = isAboveCurrentTier ? '#9ca3af' : themeColor;
  const isPastTier = !isCurrentTier && !isAboveCurrentTier;

  return (
    <div className={cn('mx-auto w-full max-w-[calc(100%-2rem)]', className)}>
      <div className="relative overflow-hidden rounded-2xl bg-white px-4 pl-8 shadow-lg drop-shadow-lg">
        {/* Left gradient bar */}
        <div
          className="absolute left-0 top-0 h-full w-8"
          style={{
            background: isAboveCurrentTier
              ? activeColor
              : `linear-gradient(to bottom, ${activeColor}50, ${activeColor})`
          }}
        >
          <div className="flex h-full rotate-180 items-center justify-center">
            <p className="text-center text-xs font-medium text-white [writing-mode:vertical-lr]">
              Membership
            </p>
          </div>
        </div>

        {/* Main content */}
        <div className="relative w-full pb-1 pl-2 pt-6">
          <div className="z-20 space-y-4">
            {/* Title */}
            <div className="flex flex-1 items-center justify-between">
              <h2 className="flex flex-nowrap items-center gap-x-2 text-lg font-normal text-gray-700 md:text-xl">
                {!hideStatusPill && (
                  <StatusPill
                    isLocked={isAboveCurrentTier}
                    isCurrent={isCurrentTier}
                    themeColor={activeColor}
                  />
                )}
                <span className="text-gray-500">
                  {brandName ? brandName + ': ' : ' '}
                </span>
                <span className="font-semibold">{rewardName}</span>
              </h2>
              {/* Points badge */}
              <div className="flex min-w-20 items-center justify-center rounded-lg bg-gray-100 p-1 px-2 text-sm">
                <span className="flex-1 font-semibold text-gray-700">
                  {formatCurrency(steppedCreditPerRm, currencyCode)} ={' '}
                </span>
                <div className="relative flex items-center rounded-lg px-1">
                  <span
                    className={cn(
                      'absolute left-[0.35rem] top-[0.20rem] z-20 text-xs font-bold text-white',
                      isBrand && 'text-black'
                    )}
                  >
                    +{valueApplied}
                  </span>
                  {isBrand ? (
                    <CoinIcon />
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="size-6 text-orange-500"
                    >
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                  )}
                </div>
              </div>
            </div>

            <div className="">
              <div
                className={cn(
                  'relative z-20 flex flex-wrap items-center gap-2'
                )}
              >
                {/* Progress bar */}
                <div className="w-[65%]">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: isPastTier
                          ? '100%'
                          : typeof rewardTotalPoints === 'number' &&
                            rewardTotalPoints > 0
                            ? `${((rewardPoints as number) / (rewardTotalPoints as number)) * 100}%`
                            : '0%',
                        backgroundColor: isPastTier ? '#4ade80' : activeColor
                      }}
                      aria-hidden="true"
                    />
                    <progress
                      value={isPastTier ? 100 : rewardPoints}
                      max={
                        typeof rewardTotalPoints === 'number'
                          ? rewardTotalPoints
                          : 100
                      }
                      className="sr-only"
                      aria-label="Reward Points Progress"
                    />
                  </div>
                </div>
                <div className={cn("text-xs font-light", isPastTier ? "font-medium text-green-500" : "text-gray-600")}>
                  {isPastTier
                    ? 'MAXIMIZED'
                    : typeof rewardTotalPoints === 'string'
                      ? '- / -'
                      : `${rewardPoints} / ${rewardTotalPoints}`}
                </div>
              </div>

              <p className="z-10 mt-3 text-xs font-medium text-gray-500">
                {description}
              </p>
              {subDescription && (
                <p className="z-10 mt-1 text-xs font-light text-gray-400">
                  {subDescription}
                </p>
              )}

              {/* Points info */}
            </div>

            {rewardAvailablePoints !== null && (
              <div className="bottom-2 right-2 z-10 space-y-1 text-sm">
                <div className="flex items-center justify-end gap-2 text-gray-400">
                  <p className="font-semibold">Available Points:</p>
                  <p className="font-bold text-gray-500">
                    {rewardAvailablePoints}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MembershipCard;
